/*
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import complaintService from '../../services/complaintService';
import LoadingSpinner from '../common/LoadingSpinner';

const statusColor = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'in_progress':
      return 'bg-blue-100 text-blue-800';
    case 'resolved':
      return 'bg-green-100 text-green-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const AdminComplaintList = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const data = await complaintService.getComplaints(1, 100);
      setComplaints(data.complaints || []);
    } catch (err) {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-6xl mx-auto mt-6 px-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">All Complaints (Admin)</h2>
        <span className="text-xs text-gray-500">
          Total: {complaints.length}
        </span>
      </div>

      {complaints.length === 0 && (
        <p className="text-gray-500 text-sm">No complaints available.</p>
      )}

      <div className="space-y-3">
        {complaints.map((c) => (
          <Link
            key={c._id}
            to={`/admin/complaints/${c._id}`}
            className="block bg-white border rounded-lg p-4 hover:shadow-sm"
          >
            <div className="flex justify-between items-center mb-1">
              <h3 className="font-semibold">{c.title}</h3>
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${statusColor(
                  c.status
                )}`}
              >
                {c.status}
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-1">
              {c.user?.name} • {c.category} • {c.priority}
            </p>
            <p className="text-sm text-gray-600 line-clamp-2">
              {c.description}
            </p>
            <p className="mt-1 text-xs text-gray-400">
              {new Date(c.createdAt).toLocaleString()}
            </p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default AdminComplaintList;
*/

import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import complaintService from '../../services/complaintService';
import LoadingSpinner from '../common/LoadingSpinner';

const statusColor = (status) => {
  switch (status) {
    case 'pending':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'in_progress':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'resolved':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'rejected':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const tabs = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'rejected', label: 'Rejected' },
];

const AdminComplaintList = () => {
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const load = async () => {
    try {
      const data = await complaintService.getComplaints(1, 100);
      setComplaints(data.complaints || []);
    } catch (err) {
      toast.error('Failed to load complaints');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredComplaints = useMemo(() => {
    if (activeTab === 'all') return complaints;
    return complaints.filter((c) => c.status === activeTab);
  }, [activeTab, complaints]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-6xl mx-auto mt-6 px-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            All Complaints
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            View and manage complaints reported by citizens across the city.
          </p>
        </div>
        <span className="inline-flex items-center text-xs text-gray-500 bg-gray-50 border border-gray-200 px-3 py-1 rounded-full">
          Total: <span className="ml-1 font-semibold">{complaints.length}</span>
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1 rounded-full text-xs sm:text-sm border whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {filteredComplaints.length === 0 && (
        <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-6 text-center mt-3">
          <p className="text-sm font-semibold text-gray-700">
            No complaints under this filter.
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Try switching the status tab to see other complaints.
          </p>
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 gap-3 mb-10">
        {filteredComplaints.map((c) => (
          <Link
            key={c._id}
            to={`/admin/complaints/${c._id}`}
            className="group bg-white border rounded-2xl p-4 hover:shadow-md hover:border-indigo-200 transition flex flex-col gap-2"
          >
            <div className="flex justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-gray-900 line-clamp-1">
                  {c.title}
                </p>
                <p className="text-[11px] text-gray-500">
                  {c.user?.name || 'Unknown citizen'} •{' '}
                  {c.user?.email || 'No email'}
                </p>
                <p className="text-[11px] text-gray-500 capitalize  ">
                  {c.category} • Priority: {c.priority}
                </p>
              </div>
                <span 
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] hover:shadow-[0_0_10px_rgba(236,72,153,0.6)] transition-shadow duration-200 border ${statusColor(c.status)}`} > {c.status} 
              </span>
            </div>
            <p className="text-xs text-gray-600 line-clamp-2">
              {c.description}
            </p>
            <div className="flex justify-between items-center text-[11px] text-gray-500">
              <span>
                {new Date(c.createdAt).toLocaleString()}
              </span>
              <span className="text-indigo-600 group-hover:underline">
                View details →
              </span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default AdminComplaintList;
